import { datadogRum } from "@datadog/browser-rum";
import { type Enterprise } from "@mg/schemas/src/commons";
import {
  type LoginResponse,
  type ShowcaseToken,
} from "@mg/schemas/src/prince/auth";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

import { posthogClient } from "../../config/posthog";
import { logout as logoutService } from "../../services/auth";
import {
  type DemoPersona,
  getStoredDemoPersona,
  setStoredDemoPersona,
} from "../demoPersona";
import { MessageType } from "../figma-plugin-types";
import { queryClient } from "../queryClient";

/**
 * Inlined mirror of `isDemoEnterprise()` from `../auth`. Importing
 * `utils/auth.ts` here creates a boot-order cycle (this slice is loaded
 * by `store.ts`, which `utils/auth.ts` transitively reaches via
 * `loginRoute`). Reads the same `["enterprises"]` react-query cache, so
 * stays in lockstep with the public helper.
 */
function isOnDemoEnterprise(value: ShowcaseToken | null): boolean {
  if (!value?.enterpriseId) return false;
  const enterprises = queryClient.getQueryData<Enterprise[]>(["enterprises"]);
  if (!enterprises) return false;
  const current = enterprises.find((e) => e._id === value.enterpriseId);
  return current?.isDemo === true;
}

/**
 * Layer the demo persona overlay (if any) on top of a decoded token.
 * The overlay only ever touches `name` and `avatar` — all auth-critical
 * fields (`userID`, `email`, `role`, `enterpriseId`, etc.) are
 * preserved. Returns the input untouched when no persona is active or
 * when the user is not currently on the demo enterprise — without this
 * gate an MG admin who sets a persona on the demo tenant and then
 * switches to a real customer tenant would still see the persona name
 * and avatar overlaid on the prod surface until logout.
 */
function withDemoPersona(value: ShowcaseToken | null): ShowcaseToken | null {
  if (!value) return value;
  if (!isOnDemoEnterprise(value)) return value;
  const persona = getStoredDemoPersona();
  if (!persona) return value;
  return {
    ...value,
    name: persona.name,
    avatar: persona.avatar ?? value.avatar,
  };
}

type AuthState = {
  /*
  The relevant fields are typically:
  - If authenticated: email, enterpriseId, exp, name, role, userID
  - If unauthenticated: enterpriseId, exp, referredBy, ticketId/folderId
  */
  value: ShowcaseToken | null;
  loggingOut: boolean;
};

const initialState: AuthState = {
  value: null,
  loggingOut: false,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    /**
     * Given a token, decodes the token and sets the user object.
     * Note: This should be invoked immediately after hitting an endpoint that
     * returns a token. This action does not check for token expiry before
     * setting the user. To check token expiry, use `derive` instead.
     */
    login(state, action: PayloadAction<LoginResponse>) {
      const decoded = jwtDecode<ShowcaseToken>(action.payload.token, {});

      // will error at this point if invalid token.

      localStorage.setItem("token", action.payload.token);
      sendTokenToServiceWorker();

      datadogRum.setUser({
        id: decoded.userID,
        ...decoded,
      });
      posthogClient.reset();
      if (decoded.userID) {
        posthogClient.identify(decoded.userID, {
          email: decoded.email,
          name: decoded.name,
          role: decoded.role,
          enterpriseId: decoded.enterpriseId,
          enterpriseName: decoded.enterpriseName ?? decoded.company,
        });
        if (decoded.enterpriseId) {
          posthogClient.group("enterprise", decoded.enterpriseId);
        }
      }
      state.value = withDemoPersona(decoded);

      // Force refetch of all user-related data
      queryClient.refetchQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          const relevantTerms = [
            "user",
            "profile",
            "comment",
            "avatar",
            "name",
            "ticket",
            "participant",
            "reviewer",
            "creative",
            "folder",
            "project",
            "message",
            "mention",
          ];
          return (
            Array.isArray(queryKey) &&
            queryKey.some(
              (key) =>
                typeof key === "string" &&
                relevantTerms.some((term) => key.toLowerCase().includes(term)),
            )
          );
        },
      });
    },
    /**
     * Removes the token from localStorage and sets the state to `null`.
     */
    logout(state, action: PayloadAction<string | undefined>) {
      // Set logging out state to prevent API calls during logout
      state.loggingOut = true;

      localStorage.removeItem("token");
      localStorage.removeItem("onboarding_flow");
      setStoredDemoPersona(null);
      sendTokenToServiceWorker();
      state.value = null;

      // Clear session cookie by calling API endpoint
      logoutService();

      // reset query client
      queryClient.invalidateQueries();
      queryClient.clear();

      // reset analytics
      posthogClient.reset();

      // reset Datadog user
      datadogRum.setUser({});

      if (action.payload != null) {
        localStorage.setItem("redirect", action.payload);
      }

      // Send logout message to iframe wrappers like the Figma plugin
      window.parent.postMessage(
        {
          type: MessageType.LOGOUT,
          sender: "web",
        },
        "*",
      );

      // Reset logging out state
      state.loggingOut = false;
    },
    derive(state) {
      // TODO: use this temporarily until parsed search is added back to router.
      const search = new URLSearchParams(window.location.search);
      const urlToken = search.get("t");
      const refreshedToken = search.get("refreshedToken");
      const localToken = localStorage.getItem("token");
      const listRedirect = search.get("r");
      const tokenIsExpired = (token: ShowcaseToken) =>
        Date.now() / 1000 > (token.exp as number);

      let defaultUser: (ShowcaseToken & { listPath?: string }) | null = null;
      if (urlToken && !tokenIsExpired(jwtDecode<ShowcaseToken>(urlToken))) {
        localStorage.setItem("urlToken", urlToken);
        search.delete("t");
      }

      function applyToken(decodedToken: ShowcaseToken, token: string) {
        defaultUser = decodedToken;
        localStorage.setItem("token", token);

        datadogRum.setUser({
          id: defaultUser.userID,
          ...defaultUser,
        });
        if (token !== localToken) {
          posthogClient.reset();
          if (decodedToken.userID) {
            posthogClient.identify(decodedToken.userID, {
              email: decodedToken.email,
              name: decodedToken.name,
              role: decodedToken.role,
              enterpriseId: decodedToken.enterpriseId,
              enterpriseName:
                decodedToken.enterpriseName ?? decodedToken.company,
            });
            if (decodedToken.enterpriseId) {
              posthogClient.group("enterprise", decodedToken.enterpriseId);
            }
          }
        }
      }

      let decodedToken: ShowcaseToken;
      // If there is a valid local token, always use that
      if (refreshedToken) {
        decodedToken = jwtDecode<ShowcaseToken>(refreshedToken);
        applyToken(decodedToken, refreshedToken);
      } else if (
        localToken &&
        (decodedToken = jwtDecode<ShowcaseToken>(localToken)) &&
        !tokenIsExpired(decodedToken) &&
        (decodedToken.email ||
          !urlToken ||
          tokenIsExpired(jwtDecode<ShowcaseToken>(urlToken)))
      ) {
        applyToken(decodedToken, localToken);
      }
      // Else if there is a valid URL token, use that
      else if (
        urlToken &&
        !tokenIsExpired((decodedToken = jwtDecode<ShowcaseToken>(urlToken)))
      ) {
        if (!decodedToken.email) {
          if (decodedToken.ticketId) {
            sessionStorage.setItem(`${decodedToken.ticketId}|token`, urlToken);
          } else if (decodedToken.folderId) {
            sessionStorage.setItem(`${decodedToken.folderId}|token`, urlToken);
          }
        }

        applyToken(decodedToken, urlToken);
      }
      // If neither is valid, remove the token
      else {
        localStorage.removeItem("token");
      }

      if (listRedirect) {
        localStorage.setItem("listPath", listRedirect);
      }

      sendTokenToServiceWorker();
      state.value = withDemoPersona(defaultUser);
      state.loggingOut = false;
    },
    /**
     * Apply or clear a demo persona overlay. Persists to localStorage
     * (so the choice survives page reloads — `derive` re-applies on
     * boot) and immediately mutates the in-memory `state.value` so the
     * sidebar and other consumers re-render in the same tick. Pass
     * `null` to remove the overlay and restore the JWT's real name +
     * avatar.
     */
    setDemoPersona(state, action: PayloadAction<DemoPersona | null>) {
      setStoredDemoPersona(action.payload);
      if (!state.value) return;
      if (action.payload === null) {
        // Re-decode from localStorage to recover the canonical name +
        // avatar that the JWT carried. Cheaper than refetching /me.
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const decoded = jwtDecode<ShowcaseToken>(token);
            state.value = {
              ...state.value,
              name: decoded.name,
              avatar: decoded.avatar,
            };
          } catch {
            // Token parse failure here means the auth slice will
            // re-derive on the next page load anyway; nothing to do.
          }
        }
        return;
      }
      state.value = {
        ...state.value,
        name: action.payload.name,
        avatar: action.payload.avatar ?? state.value.avatar,
      };
    },
  },
});

export const { derive, login, logout, setDemoPersona } = authSlice.actions;
